import _styled from "@emotion/styled";

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { ThemeProvider as EmotionThemeProvider, useTheme as emotionUseTheme } from 'emotion-theming';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import React, { createContext, useContext } from 'react';
import { accountGroupNavTheme } from './components/nav/accountGroup';
import { navThemeFromPartnerTheme } from './components/nav/partner';
import { en, zh } from './localized';
import { getBrandedColor } from './partner';
import { buttonColorThemeSchema } from './components/button';
import { textInputColorThemeSchema } from './components/textInput';
import { getComponentTheme } from './components';
import { jsx as ___EmotionJSX } from "@emotion/core";
export * from './globalStyles'; // used by story and by webapp container to construct navTheme based on partner values

export { navThemeFromPartnerTheme };

var getPartnerThemeOverride = function getPartnerThemeOverride(partnerTheme) {
  var partnerNavTheme = partnerTheme.navTheme && navThemeFromPartnerTheme(partnerTheme.navTheme);
  return _objectSpread(_objectSpread({}, partnerNavTheme ? {
    nav: partnerNavTheme
  } : null), {}, {
    partnerLogoUrl: partnerTheme.logoUrl
  });
};

var getAccountGroupThemeOverride = function getAccountGroupThemeOverride() {
  return {
    nav: accountGroupNavTheme
  };
}; // just exported for testing/docs; try to use ThemeProvider instead


export var getTheme = function getTheme(options) {
  var langKey = options.langKey,
      _options$contextType = options.contextType,
      contextType = _options$contextType === void 0 ? 'default' : _options$contextType;
  var baseTheme = {
    en: en,
    zh: zh
  }[langKey];

  if (contextType === 'account-group') {
    return _objectSpread(_objectSpread({}, baseTheme), getAccountGroupThemeOverride());
  }

  return _objectSpread(_objectSpread({}, baseTheme), options.partnerTheme ? getPartnerThemeOverride(options.partnerTheme) : null);
}; // use this to provide the right theme

export var ThemeProvider = function ThemeProvider(_ref) {
  var langKey = _ref.langKey,
      contextType = _ref.contextType,
      partnerTheme = _ref.partnerTheme,
      other = _objectWithoutProperties(_ref, ["langKey", "contextType", "partnerTheme"]);

  return ___EmotionJSX(EmotionThemeProvider, _extends({
    theme: getTheme({
      langKey: langKey,
      contextType: contextType,
      partnerTheme: partnerTheme
    })
  }, other));
};
export var PartnerThemeConfigContext = /*#__PURE__*/createContext(undefined);
export var PartnerThemeConfigProvider = function PartnerThemeConfigProvider(_ref2) {
  var value = _ref2.value,
      etc = _objectWithoutProperties(_ref2, ["value"]);

  return ___EmotionJSX(PartnerThemeConfigContext.Provider, _extends({
    value: value
  }, etc));
};
export var PartnerBrandingThemeMapper = function PartnerBrandingThemeMapper(props) {
  var partnerTheme = useContext(PartnerThemeConfigContext);
  return ___EmotionJSX(EmotionThemeProvider, _extends({
    theme: function theme(parentTheme) {
      var brandColor = getBrandedColor({
        partnerTheme: partnerTheme,
        defaultColors: parentTheme.colors
      });
      return _objectSpread(_objectSpread(_objectSpread({}, parentTheme), brandColor), {}, {
        // re-generate the button theme,
        // this is for compatibility where Button is wrapped in PartnerBrandingThemeMapper (e.g. TopNavigation)
        button: getComponentTheme({
          componentType: 'button',
          colors: brandColor === null || brandColor === void 0 ? void 0 : brandColor.colors
        })
      });
    }
  }, props));
};
/**
 * Theme FEC per component by component level color tokens using getXXXTheme() function with configured colors.
 * This can avoid generally applying color theme to all components at one time.
 * Once a component is ready to be themed (break down into color tokens), its colors merge can be added to this Mapper.
 */

export var PartnerComponentThemeProvider = function PartnerComponentThemeProvider(props) {
  var themeColors = props.themeColors,
      rest = _objectWithoutProperties(props, ["themeColors"]);

  return ___EmotionJSX(EmotionThemeProvider, _extends({
    theme: function theme(parentTheme) {
      var customTheme = cloneDeep(parentTheme); // Global colors

      var globalCustomColors = merge({}, customTheme.colors, themeColors === null || themeColors === void 0 ? void 0 : themeColors.global); // Only apply to the components that support component-level theming
      // Override by component level configuration

      customTheme.button = getComponentTheme({
        componentType: 'button',
        colors: globalCustomColors,
        colorsOverride: themeColors === null || themeColors === void 0 ? void 0 : themeColors.button
      });
      customTheme.textInput = getComponentTheme({
        componentType: 'textInput',
        colors: globalCustomColors,
        colorsOverride: themeColors === null || themeColors === void 0 ? void 0 : themeColors.textInput
      });
      return customTheme;
    }
  }, rest));
}; // as per emotion guidelines.  Use these whenever you want to use this theme (type safely)

export var styled = _styled;
export var useTheme = emotionUseTheme; // helper to default theme to themes.en
// - temporary until all common component users have added ThemeProvider

export var withFallback = function withFallback(theme) {
  return !theme || isEmpty(theme) ? getTheme({
    langKey: 'en'
  }) : theme;
};
/**
 * Export component color schema, used to build color configuration tool
 */

export { buttonColorThemeSchema, textInputColorThemeSchema };